<template>
  <div>
    <DatabaseStats
      :tables="databaseTables"
      :stats="dbStats || {}"
      :is-loading="isLoadingStats"
      :is-clearing="isClearingTable"
      @clear-table="confirmClearTable"
      @clear-all="confirmClearAll"
      @refresh="refetchStats"
    />

    <!-- Clear Confirm Modal -->
    <ClearConfirmModal
      :open="showClearModal"
      :table="tableToClear"
      :record-count="tableToClear ? (dbStats?.[tableToClear.key] ?? 0) : 0"
      :clear-all-mode="clearAllMode"
      :is-clearing="isClearingTable"
      @close="showClearModal = false"
      @confirm="executeClear"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { adminService } from '@/services/adminService.js'

import DatabaseStats from '@/components/admin/DatabaseManagement/DatabaseStats.vue'
import ClearConfirmModal from '@/components/admin/DatabaseManagement/ClearConfirmModal.vue'

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false
  }
})

const databaseTables = [
  {
    key: 'employees',
    label: 'Medewerkers',
    description: 'Simplicate medewerker gegevens',
    cascadeWarning: 'Dit verwijdert ook alle uren, toewijzingen en notities van medewerkers.'
  },
  {
    key: 'hours',
    label: 'Uren',
    description: 'Ingeboekte uren uit Simplicate'
  },
  {
    key: 'prospects_leads',
    label: 'Prospects & Leads',
    description: 'Sales pipeline uit Simplicate',
    cascadeWarning: 'Dit verwijdert ook alle toewijzingen, voorkeuren en acties.'
  },
  {
    key: 'project_assignments',
    label: 'Toewijzingen',
    description: 'Medewerker toewijzingen aan projecten'
  },
  {
    key: 'prospect_employee_preferences',
    label: 'Voorkeuren',
    description: 'Medewerker voorkeuren per prospect'
  },
  {
    key: 'prospect_actions',
    label: 'Acties',
    description: 'Actielijst items'
  },
  {
    key: 'teams',
    label: 'Teams',
    description: 'Team definities uit Simplicate',
    cascadeWarning: 'Dit verwijdert ook alle team-medewerker koppelingen.'
  },
  {
    key: 'sync_logs',
    label: 'Sync Logs',
    description: 'Synchronisatie logboek'
  }
]

const showClearModal = ref(false)
const tableToClear = ref(null)
const clearAllMode = ref(false)
const isClearingTable = ref(false)

// Fetch database stats
const {
  data: dbStats,
  isLoading: isLoadingStats,
  refetch: refetchStats
} = useQuery({
  queryKey: ['database-stats'],
  queryFn: () => adminService.getDatabaseStats(),
  staleTime: 1000 * 30,
  enabled: computed(() => props.isActive)
})

const confirmClearTable = table => {
  tableToClear.value = table
  clearAllMode.value = false
  showClearModal.value = true
}

const confirmClearAll = () => {
  tableToClear.value = null
  clearAllMode.value = true
  showClearModal.value = true
}

const executeClear = async () => {
  isClearingTable.value = true

  try {
    if (clearAllMode.value) {
      await adminService.clearAllData()
    } else if (tableToClear.value) {
      await adminService.clearTable(tableToClear.value.key)
    }

    await refetchStats()
    showClearModal.value = false
    tableToClear.value = null
    clearAllMode.value = false
  } catch (err) {
    console.error('Error clearing data:', err)
    alert(err.response?.data?.error || 'Fout bij wissen van gegevens')
  } finally {
    isClearingTable.value = false
  }
}
</script>
